import {
	BidBondAmountType,
	type CompanyContactId,
	ContractSuretyType,
	Currency,
	type FinalContractSuretyBondFormType,
	type ISODate,
	type NaicsCodeId,
	percentSchemaV2,
	type SuretyBondId,
	SuretyType,
	type UploadedFile,
} from "@inrev/common";
import { transformBondFormForSubmission } from "@portal/views/shared/bond/bondUtils";
import type { BondFormData } from "../../../../types";

export type ConfirmedValues = {
	projectName?: string;
	contractAmount?: Currency;
	bondAmount?: Currency;
	bidAmount?: Currency;
	bondAmountPercentOfBid?: number;
	scopeOfWork?: NaicsCodeId[];
	completionBond?: boolean;
	tListingRequired?: boolean;
	bidDate?: ISODate;
	finalBondFormType?: FinalContractSuretyBondFormType;
	bidBondId?: SuretyBondId;
	actualBidAmount?: Currency;
	actualBidDate?: ISODate;
	secondBid?: Currency;
};

// percentSchemaV2 requires a 0-1 fraction in 0.001 increments. Float division
// (e.g. 5.2 / 100) and out-of-range input would otherwise fail .parse().
export const normalizePercentFraction = (fraction: number): number =>
	Math.min(1, Math.max(0, Math.round(fraction * 1000) / 1000));

export function buildCreateQuotePayload({
	contractSuretyType,
	principal,
	principalName,
	confirmedValues,
	uploadedFiles = [],
	comment,
	bondForm,
}: {
	contractSuretyType: ContractSuretyType;
	principal: CompanyContactId;
	principalName: string;
	confirmedValues: ConfirmedValues;
	uploadedFiles?: UploadedFile[];
	comment?: string;
	bondForm?: BondFormData;
}) {
	const baseFields = {
		suretyType: SuretyType.contract,
		principal: { id: principal, name: principalName },
		additionalInfo: { comment: comment?.trim() || undefined, attachments: uploadedFiles },
		bondForm: bondForm ? transformBondFormForSubmission(bondForm) : null,
		completionBond: confirmedValues.completionBond,
		tListingRequired: confirmedValues.tListingRequired,
		project: { name: confirmedValues.projectName, scopeOfWork: confirmedValues.scopeOfWork },
	};

	if (contractSuretyType === ContractSuretyType.final) {
		return {
			quote: {
				...baseFields,
				contractSuretyType: ContractSuretyType.final,
				bondAmount: confirmedValues.bondAmount ?? Currency.ZERO,
				contractAmount: confirmedValues.contractAmount ?? Currency.ZERO,
			},
		};
	}

	if (contractSuretyType === ContractSuretyType.bid_to_final) {
		if (!confirmedValues.bidBondId) {
			throw new Error("bidBondId is required for a bid_to_final submission");
		}
		if (!confirmedValues.actualBidAmount) {
			throw new Error("actualBidAmount is required for a bid_to_final submission");
		}
		const { completionBond: _completionBond, ...baseFieldsForBidToFinal } = baseFields;
		return {
			quote: {
				...baseFieldsForBidToFinal,
				contractSuretyType: ContractSuretyType.bid_to_final,
				bidBondId: confirmedValues.bidBondId,
				actualBidAmount: confirmedValues.actualBidAmount,
				actualBidDate: confirmedValues.actualBidDate,
				secondBid: confirmedValues.secondBid,
				bondAmount: confirmedValues.bondAmount ?? Currency.ZERO,
				contractAmount: confirmedValues.contractAmount ?? Currency.ZERO,
			},
		};
	}

	const bidAmount = confirmedValues.bidAmount ?? Currency.ZERO;
	const bondAmountPercentOfBid = percentSchemaV2.parse(
		normalizePercentFraction(confirmedValues.bondAmountPercentOfBid ?? 0),
	);
	const bondAmount = Currency.round(bidAmount * bondAmountPercentOfBid);
	return {
		quote: {
			...baseFields,
			contractSuretyType: ContractSuretyType.bid,
			bondAmount,
			bidAmount,
			bondAmountPercentOfBid,
			bidBondAmountType: BidBondAmountType.percent,
			bidDate: confirmedValues.bidDate,
			finalBondFormType: confirmedValues.finalBondFormType,
		},
	};
}
